import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { gameService } from '../services/gameService';
import { GameSpaceInfo } from '../types';
import { useAuth } from './AuthContext';

interface GameSpaceContextType {
  gameSpaces: GameSpaceInfo[];
  loading: boolean;
  error: string | null;
  refreshGameSpaces: () => Promise<void>;
  getGameSpaceById: (id: number) => GameSpaceInfo | undefined;
}

const GameSpaceContext = createContext<GameSpaceContextType | undefined>(undefined);

export const GameSpaceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [gameSpaces, setGameSpaces] = useState<GameSpaceInfo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refreshGameSpaces = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await gameService.getUserGameSpaces();
      if (response.success) {
        setGameSpaces(response.data);
      } else {
        setError(response.message || 'Failed to load game spaces');
      }
    } catch (err) {
      console.error('Error fetching game spaces:', err);
      setError('An unexpected error occurred while loading game spaces');
    } finally {
      setLoading(false);
    }
  };

  const getGameSpaceById = (id: number) => {
    return gameSpaces.find(space => space.id === id);
  };

  useEffect(() => {
    // Only fetch game spaces if the user is authenticated
    // This prevents 401 errors and infinite login redirects for unauthenticated users
    if (isAuthenticated) {
      refreshGameSpaces();
    } else {
      // If not authenticated, clear loading state to avoid showing loading spinner indefinitely
      setLoading(false);
    }
  }, [isAuthenticated]);

  const value = {
    gameSpaces,
    loading,
    error,
    refreshGameSpaces,
    getGameSpaceById
  };

  return (
    <GameSpaceContext.Provider value={value}>
      {children}
    </GameSpaceContext.Provider>
  );
};

export const useGameSpace = (): GameSpaceContextType => {
  const context = useContext(GameSpaceContext);
  if (context === undefined) {
    throw new Error('useGameSpace must be used within a GameSpaceProvider');
  }
  return context;
};
